import { ReactNode } from 'react';

/**
 * The design's KPI card (OO1): an uppercase letter-spaced eyebrow, the number in Oswald,
 * a quiet sub-line. `highlight` is the design's dark block (solid Apex black - the design
 * system forbids gradients as backgrounds).
 */
interface StatsCardProps {
  label: string;
  value: string | number;
  subValue?: string;
  icon?: ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  variant?: 'default' | 'highlight';
}

export const StatsCard = ({
  label,
  value,
  subValue,
  icon,
  trend,
  variant = 'default',
}: StatsCardProps) => {
  const isHighlight = variant === 'highlight';

  return (
    <div
      className={`
        rounded-xl px-[18px] py-4
        ${isHighlight
          ? 'bg-gray-900 text-white'
          : 'bg-white border border-gray-200'
        }
      `}
    >
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className={`text-[9.5px] font-bold uppercase tracking-[.13em] ${isHighlight ? 'text-primary-400' : 'text-gray-400'}`}>
            {label}
          </p>
          <p className={`font-display text-[26px] leading-none font-semibold tabular-nums mt-2 ${isHighlight ? 'text-white' : 'text-gray-900'}`}>
            {value}
          </p>
          {subValue && (
            <p className={`text-[11.5px] mt-1.5 ${isHighlight ? 'text-white/60' : 'text-gray-500'}`}>
              {subValue}
            </p>
          )}
          {trend && (
            <div className={`flex items-center mt-2 text-xs font-medium ${
              trend.isPositive
                ? isHighlight ? 'text-success-wash' : 'text-success'
                : isHighlight ? 'text-primary-300' : 'text-primary-500'
            }`}>
              {trend.isPositive ? (
                <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
              ) : (
                <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 13l-5 5m0 0l-5-5m5 5V6" />
                </svg>
              )}
              {Math.abs(trend.value)}%
            </div>
          )}
        </div>
        {icon && (
          <div className={`p-2 rounded-lg ${isHighlight ? 'bg-white/10' : 'bg-gray-100'}`}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};

// Metric display for property details: a quiet label over a semibold value.
interface MetricProps {
  label: string;
  value: string | number | undefined | null;
  format?: 'currency' | 'number' | 'percent' | 'text';
  size?: 'sm' | 'md' | 'lg';
}

export const Metric = ({ label, value, format = 'text', size = 'md' }: MetricProps) => {
  const formatValue = () => {
    if (value === null || value === undefined) return '—';

    switch (format) {
      case 'currency':
        return typeof value === 'number'
          ? `$${value.toLocaleString()}`
          : value;
      case 'number':
        return typeof value === 'number'
          ? value.toLocaleString()
          : value;
      case 'percent':
        return `${value}%`;
      default:
        return value;
    }
  };

  const sizeStyles = {
    sm: { label: 'text-[10.5px]', value: 'text-[13px]' },
    md: { label: 'text-[11px]', value: 'text-[13.5px]' },
    lg: { label: 'text-[11px]', value: 'font-display text-[19px]' },
  };

  return (
    <div>
      <dt className={`${sizeStyles[size].label} text-gray-500 font-normal`}>{label}</dt>
      <dd className={`${sizeStyles[size].value} text-gray-800 font-medium tabular-nums mt-1`}>
        {formatValue()}
      </dd>
    </div>
  );
};
